import 'focus-visible';
import Alpine from 'alpinejs';
import Event from '../../event/js/module';
import KeyboardEvent from '../../event/js/keyboard';

// constants
const ARIA_EXPANDED_ATTR = 'aria-expanded';
const ARIA_LABELLEDBY_ATTR = 'aria-labelledby';
const DISPLAY_NONE_CLASS = 'at-d-none';
const COLLAPSING_CLASS = '.delayed-collapsing';
const EXPANDED_ACCORDION_CLASS = 'at-universal-accordion--expanded';
const SCREEN_BREAKPOINT = 1280;
const DESKTOP_Y_OFFSET = 48;
const MOBILE_Y_OFFSET = 32;

// this function is used to trigger a resize event to render components within an accordion item correctly
const triggerResizeEvent = (timeout = 200) => {
  setTimeout(() => {
    const resizeEvent = document.createEvent('HTMLEvents');
    resizeEvent.initEvent('resize', true, false);
    window.dispatchEvent(resizeEvent);
  }, timeout);
};

const scrollToElementWithOffset = (sectionId, offset) => {
  const target = document.getElementById(sectionId);
  if (!target) return;

  const targetPosition = target.getBoundingClientRect().top + window.pageYOffset;
  let offsetValue;

  if (typeof offset === 'number') {
    offsetValue = offset;
  } else if (typeof offset === 'object') {
    const isDesktop = window.innerWidth >= (offset.breakpoint || SCREEN_BREAKPOINT);
    offsetValue = isDesktop ? (offset.desktop || DESKTOP_Y_OFFSET) : (offset.mobile || MOBILE_Y_OFFSET);
  } else {
    offsetValue = MOBILE_Y_OFFSET;
  }

  const offsetPosition = targetPosition - offsetValue;

  window.scrollTo({
    top: offsetPosition,
    behavior: 'smooth',
  });
};

function filterAccordionData() {
  return {
    filtersVisible: false,

    getFilterButtonText() {
      const count = Alpine.store('filters')?.count || 0;

      return count > 0 ? `Filters (${count})` : 'Filters';
    },

    init() {},
  };
}

const scrollToProjectCards = (sectionId) => {
  scrollToElementWithOffset(sectionId, MOBILE_Y_OFFSET);
};

const scrollToAllProjects = (sectionId) => {
  scrollToElementWithOffset(sectionId, { mobile: MOBILE_Y_OFFSET, desktop: DESKTOP_Y_OFFSET, breakpoint: SCREEN_BREAKPOINT });
};

const initAccordion = (element = 'accordion', showAllElement = 'accordion-show-hide-all-btn', showResultsElement = 'accordion-show-results-btn') => {
  window.onload = () => {
    const accordionItemsToCollapse = document.querySelectorAll(COLLAPSING_CLASS);
    if (!accordionItemsToCollapse) {
      return;
    }

    accordionItemsToCollapse.forEach((item) => {
      const accordionId = item.getAttribute(ARIA_LABELLEDBY_ATTR);
      const relatedHeader = document.querySelector(`#${accordionId}`);
      const isExpanded = relatedHeader.getAttribute(ARIA_EXPANDED_ATTR) === 'true';

      if (isExpanded) {
        return;
      }

      setTimeout(() => {
        item.classList.add(DISPLAY_NONE_CLASS);
        relatedHeader.setAttribute(ARIA_EXPANDED_ATTR, false);
      }, 200);
    });
  };

  Event.eventHandler(
    showAllElement,
    'click',
    (buttonElement, _, event) => {
      event.preventDefault();

      const isExpanded = buttonElement.innerText === 'Hide all';

      const accordionBtnSelector = `[data-element="${element}"]`;
      const parentElement = buttonElement.parentElement.parentElement;

      parentElement.querySelectorAll(accordionBtnSelector).forEach((accordionBtn) => {
        const relatedPanel = document.querySelector(accordionBtn.getAttribute(`data-${element}-target`));

        accordionBtn.setAttribute(ARIA_EXPANDED_ATTR, !isExpanded);

        if (isExpanded) {
          relatedPanel.classList.add(DISPLAY_NONE_CLASS);
          accordionBtn.classList.remove(EXPANDED_ACCORDION_CLASS);
          const button = buttonElement;
          button.innerText = 'Show all';
        } else {
          relatedPanel.classList.remove(DISPLAY_NONE_CLASS);
          accordionBtn.classList.add(EXPANDED_ACCORDION_CLASS);
          const button = buttonElement;
          button.innerText = 'Hide all';
          triggerResizeEvent();
        }
      });
    },
  );

  Event.eventHandler(element, 'click', (buttonElement, panelElement, event) => {
    event.preventDefault();

    const isExpanded = buttonElement.getAttribute('aria-expanded') === 'true';

    buttonElement.setAttribute('aria-expanded', !isExpanded);

    if (isExpanded) {
      panelElement.classList.add('at-d-none');
      buttonElement.classList.remove('at-universal-accordion--expanded');
    } else {
      panelElement.classList.remove('at-d-none');
      buttonElement.classList.add('at-universal-accordion--expanded');
      triggerResizeEvent();
    }
  });

  Event.eventHandler(
    element,
    'keydown',
    (buttonElement, panelElement, event) => {
      switch (true) {
        case KeyboardEvent.CheckKey(event, KeyboardEvent.Key.UP):
          event.preventDefault();
          event.stopPropagation();

          buttonElement.parentElement.previousElementSibling?.previousElementSibling?.firstElementChild.focus();
          break;
        case KeyboardEvent.CheckKey(event, KeyboardEvent.Key.DOWN):
          event.preventDefault();
          event.stopPropagation();

          panelElement?.nextElementSibling?.firstElementChild.focus();
          break;
        default:
          break;
      }
    },
  );

  Event.eventHandler(
    showResultsElement,
    'click',
    (buttonElement, _, event) => {
      event.preventDefault();
      const targetId = buttonElement.getAttribute(`data-${showResultsElement}-target`);
      if (targetId) {
        const cleanTargetId = targetId.replace('#', '');
        scrollToProjectCards(cleanTargetId);
      }
    },
  );

  Event.eventHandler(
    'accordion-back-to-all-projects-btn',
    'click',
    (buttonElement, _, event) => {
      event.preventDefault();
      const targetId = buttonElement.getAttribute('data-accordion-back-to-all-projects-btn-target');
      if (targetId) {
        const cleanTargetId = targetId.replace('#', '');
        scrollToAllProjects(cleanTargetId);
      }
    },
  );
};

const initFilterCheckboxHandler = () => {
  if (document.hasFilterCheckboxHandler) {
    return;
  }
  document.hasFilterCheckboxHandler = true;

  document.addEventListener('change', (event) => {
    if (event.target.type !== 'checkbox') {
      return;
    }

    const checkboxWrapper = event.target.closest('.at-project-filter-checkbox-wrapper');
    if (!checkboxWrapper) {
      return;
    }
    const filterType = checkboxWrapper.dataset.filterType;
    const filterValue = checkboxWrapper.dataset.filterValue;

    if (!filterType || !filterValue) {
      return;
    }

    let element = checkboxWrapper;
    while (element && element !== document.body) {
      if (element.__x) {
        const alpineData = element.__x.$data();
        if (alpineData && typeof alpineData.toggleFilter === 'function') {
          alpineData.toggleFilter(filterType, filterValue, event);
          setTimeout(() => {
            document.dispatchEvent(new CustomEvent('filterChanged'));
          }, 0);
          return;
        }
      }

      if (typeof window.Alpine !== 'undefined' && window.Alpine.$data) {
        const alpineData = window.Alpine.$data(element);
        if (alpineData && typeof alpineData.toggleFilter === 'function') {
          alpineData.toggleFilter(filterType, filterValue, event);
          setTimeout(() => {
            document.dispatchEvent(new CustomEvent('filterChanged'));
          }, 0);
          return;
        }
      }

      element = element.parentElement;
    }
  });
};

if (typeof window !== 'undefined') {
  window.scrollToElementWithOffset = scrollToElementWithOffset;
  window.scrollToProjectCards = scrollToProjectCards;
  window.scrollToAllProjects = scrollToAllProjects;
  window.filterAccordionData = filterAccordionData;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFilterCheckboxHandler);
  } else {
    initFilterCheckboxHandler();
  }
}

initAccordion.scrollToElementWithOffset = scrollToElementWithOffset;
initAccordion.scrollToProjectCards = scrollToProjectCards;
initAccordion.scrollToAllProjects = scrollToAllProjects;
initAccordion.filterAccordionData = filterAccordionData;
initAccordion.initFilterCheckboxHandler = initFilterCheckboxHandler;

export default initAccordion;